'use client';

import Link from 'next/link';
import { useBasePath } from '../navigation/base-path';
import { InfiniteScrollTrigger } from './infinite-scroll-trigger';
import { S3Image } from './s3-image';
import { VideoThumbnail } from './video-thumbnail';
import { cn } from '../utils/cn';

export type HomeHistoryFilter = 'photo' | 'video' | 'music' | 'chat';

export interface HomeHistoryTabItem {
  id: string;
  resultUrl?: string | null;
  thumbnailUrl?: string | null;
  resultType?: 'IMAGE' | 'VIDEO' | 'AUDIO' | null;
  status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | string | null;
  prompt?: string | null;
  resultWidth?: number | null;
  resultHeight?: number | null;
  feedStatus?: 'APPROVED' | 'PENDING_MODERATION' | string | null;
}

export interface HomeHistoryConversationItem {
  id: string;
  title?: string | null;
  modelName?: string | null;
}

interface HomeHistoryTabContentProps {
  filter: HomeHistoryFilter;
  history?: HomeHistoryTabItem[];
  conversations?: HomeHistoryConversationItem[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onFilterChange: (filter: HomeHistoryFilter) => void;
  onLoadMore: () => void;
}

const historyFilters: { id: HomeHistoryFilter; label: string }[] = [
  { id: 'photo', label: 'Фото' },
  { id: 'video', label: 'Видео' },
  { id: 'music', label: 'Музыка' },
  { id: 'chat', label: 'Чат' },
];

const HISTORY_SKELETON_LEFT = [200, 160, 230, 180];
const HISTORY_SKELETON_RIGHT = [160, 230, 180, 200];

export function HomeHistoryTabContent({
  filter,
  history,
  conversations,
  isLoading = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onFilterChange,
  onLoadMore,
}: HomeHistoryTabContentProps) {
  const filteredHistory = history?.filter((item) => {
    if (filter === 'photo') {
      return item.resultType === 'IMAGE' || (!item.resultType && item.status !== 'COMPLETED');
    }
    if (filter === 'video') return item.resultType === 'VIDEO';
    if (filter === 'music') return item.resultType === 'AUDIO';
    return false;
  });

  return (
    <div className="space-y-4">
      <div className="mx-4">
        <div className="flex rounded-[20px] bg-[#25272a] p-[3px]">
          {historyFilters.map((filterItem) => (
            <button
              key={filterItem.id}
              onClick={() => onFilterChange(filterItem.id)}
              className={cn(
                'flex-1 rounded-[18px] py-2 text-xs font-semibold transition-all',
                filter === filterItem.id ? 'bg-[#fafafa] text-[#0f0f0f]' : 'text-[#fafafa]',
              )}
            >
              {filterItem.label}
            </button>
          ))}
        </div>
      </div>

      {filter === 'chat' && <ChatConversations conversations={conversations} />}

      {(filter === 'photo' || filter === 'video' || filter === 'music') && (
        <>
          {isLoading ? (
            <HistoryMasonrySkeleton />
          ) : !filteredHistory || filteredHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm text-muted-foreground">
                {filter === 'photo'
                  ? 'Нет сгенерированных фото'
                  : filter === 'video'
                    ? 'Нет сгенерированных видео'
                    : 'Нет сгенерированной музыки'}
              </p>
            </div>
          ) : (
            <>
              <HistoryMasonry items={filteredHistory} />
              <InfiniteScrollTrigger
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={onLoadMore}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function AudioHistoryThumb({
  thumbnailUrl,
  prompt,
}: {
  thumbnailUrl?: string | null;
  prompt?: string | null;
}) {
  return (
    <div className="relative flex aspect-square w-full flex-col items-center justify-center overflow-hidden bg-[#25272a]">
      {thumbnailUrl ? (
        <S3Image
          src={thumbnailUrl}
          alt={prompt || 'Music cover'}
          width={390}
          height={390}
          className="absolute inset-0 h-full w-full object-cover opacity-75"
        />
      ) : null}
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative z-[1] flex h-14 w-14 items-center justify-center rounded-full bg-white/15 backdrop-blur">
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      </div>
      {prompt ? (
        <p className="relative z-[1] mt-3 line-clamp-2 max-w-[80%] text-center text-xs font-medium text-white">
          {prompt}
        </p>
      ) : null}
    </div>
  );
}

function ChatConversations({
  conversations,
}: {
  conversations?: HomeHistoryConversationItem[];
}) {
  const basePath = useBasePath();
  return (
    <div className="space-y-2 px-4">
      {!conversations || conversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/5">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8e8e93"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <p className="text-sm text-muted-foreground">Нет чатов</p>
          <p className="mt-1 text-xs text-muted-foreground/60">
            Начните диалог через панель генерации
          </p>
        </div>
      ) : (
        conversations.map((conversation) => (
          <Link
            key={conversation.id}
            href={`${basePath}/chat/${conversation.id}`}
            className="flex items-center gap-3 rounded-xl bg-white/[0.05] p-4"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">
                {conversation.title || 'Новый чат'}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {conversation.modelName || 'ChatGPT'}
              </p>
            </div>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8e8e93"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
        ))
      )}
    </div>
  );
}

function HistoryMasonrySkeleton() {
  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">
        {HISTORY_SKELETON_LEFT.map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {HISTORY_SKELETON_RIGHT.map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
    </div>
  );
}

function HistoryMasonry({ items }: { items: HomeHistoryTabItem[] }) {
  const [left, right] = balanceMasonryColumns(items);
  const basePath = useBasePath();

  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">
        {left.map((item) => renderHistoryItem(item, basePath))}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {right.map((item) => renderHistoryItem(item, basePath))}
      </div>
    </div>
  );
}

function renderHistoryItem(item: HomeHistoryTabItem, basePath: string) {
  const isCompleted = item.resultUrl && item.status === 'COMPLETED';
  const cardStyle =
    isCompleted && item.resultType === 'IMAGE'
      ? undefined
      : item.resultWidth && item.resultHeight
        ? { aspectRatio: `${item.resultWidth}/${item.resultHeight}` }
        : { minHeight: 120 };

  return (
    <Link
      key={item.id}
      href={`${basePath}/history/${item.id}?source=history`}
      className="relative block overflow-hidden bg-card"
      style={cardStyle}
    >
      {isCompleted ? (
        item.resultType === 'VIDEO' ? (
          <VideoThumbnail
            src={item.resultUrl!}
            poster={item.thumbnailUrl}
            alt={item.prompt || 'Video'}
            iconSize="lg"
          />
        ) : item.resultType === 'AUDIO' ? (
          <AudioHistoryThumb thumbnailUrl={item.thumbnailUrl} prompt={item.prompt} />
        ) : (
          <S3Image
            src={item.thumbnailUrl || item.resultUrl!}
            alt={item.prompt || 'Generated'}
            width={item.resultWidth || 390}
            height={item.resultHeight || 390}
            className="block h-auto w-full"
          />
        )
      ) : item.status === 'FAILED' ? (
        <div className="relative aspect-square w-full bg-[#4e4e50]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/error-icon.svg"
            alt=""
            className="absolute left-1/2 top-[13%] w-[35%] -translate-x-1/2"
          />
          <p className="absolute bottom-[16%] left-0 right-0 px-3 text-center font-sans text-xs leading-[1.75] tracking-tight text-white">
            Что-то пошло не так
          </p>
          <p className="absolute bottom-[6%] left-0 right-0 text-center font-sans text-xs font-semibold text-[#ef6444]">
            Повторить
          </p>
        </div>
      ) : (
        <div className="relative aspect-square w-full bg-[#25272a]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/logo-full-gray.svg"
            alt=""
            className="absolute left-1/2 top-[22%] w-[36%] -translate-x-1/2"
          />
          <div className="absolute left-1/2 top-[76%] flex -translate-x-1/2 gap-[7px]">
            <span
              className="h-[7px] w-[7px] rounded-full bg-accent animate-bounce-dot"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="h-[7px] w-[7px] rounded-full bg-accent animate-bounce-dot"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="h-[7px] w-[7px] rounded-full bg-accent animate-bounce-dot"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        </div>
      )}
      {item.feedStatus === 'APPROVED' && (
        <span
          className="absolute right-0 top-0 flex items-center justify-center rounded-bl-[10px] bg-[#9dd94a]"
          style={{ padding: '2px 2px 4px 5px', height: 21 }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 21 21"
            fill="none"
            className="shrink-0"
            style={{ width: 14, height: 14 }}
          >
            <circle cx="10.5" cy="10.5" r="9" stroke="#fff" strokeWidth="1.4" />
            <path
              d="M6.44 10.46l2.72 2.72 5.44-5.44"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
      {item.feedStatus === 'PENDING_MODERATION' && (
        <span className="absolute right-0 top-0 z-10 rounded-bl-[10px] bg-[#25272a] px-2 pb-1 pt-0.5 text-[11px] font-medium text-white">
          На модерации
        </span>
      )}
    </Link>
  );
}

function balanceMasonryColumns<
  T extends { resultWidth?: number | null; resultHeight?: number | null },
>(items: T[]): [T[], T[]] {
  const left: T[] = [];
  const right: T[] = [];
  let leftHeight = 0;
  let rightHeight = 0;

  for (const item of items) {
    const ratio =
      item.resultWidth && item.resultHeight ? item.resultHeight / item.resultWidth : 1;
    if (leftHeight <= rightHeight) {
      left.push(item);
      leftHeight += ratio;
    } else {
      right.push(item);
      rightHeight += ratio;
    }
  }

  return [left, right];
}
